import { Component, computed, inject, input, TemplateRef } from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { HlmTd } from '@spartan-ng/helm/table';
import { OcpTableData } from 'shared';

import {
  getResolvedColumnWidth,
  TableColumnConfig,
} from '../../../table-header/table-header.interface';
import { FieldValuePipe } from '../../pipes/field-value.pipe';

@Component({
  selector: 'ocp-table-row-cell',
  imports: [NgTemplateOutlet, TranslatePipe, FieldValuePipe, HlmTd],
  template: `
    <td
      hlmTableCell
      [class]="column().cssClass"
      [style.width]="columnWidth()"
      [style.min-width]="column().minWidth"
      [style.max-width]="columnWidth()"
      [style.overflow]="'hidden'"
      class="table-row-cell"
      (click)="column().stopClickPropagation ? $event.stopPropagation() : null"
    >
      <div class="truncated-text">
        @if (cellTemplate()) {
          <ng-container *ngTemplateOutlet="cellTemplate()!; context: { $implicit: rowData() }" />
        } @else {
          @if (rowData() | fieldValue: column().field) {
            {{ rowData() | fieldValue: column().field }}
          } @else {
            <span class="table-row-cell__muted">{{ 'tasks-list.not-set' | translate }}</span>
          }
        }
      </div>
    </td>
  `,
  styles: `
    :host {
      display: contents;
    }

    /* Table cells resist shrinking below their content min-content width unless overflow
       is non-visible; without this, a cell content sets an implicit floor on the column
       that the bound width/min-width above cannot override. */
    .table-row-cell {
      overflow: hidden;
    }

    .table-row-cell__muted {
      opacity: 0.5;
      font-size: var(--font-size-base);
    }
  `,
})
export class TableRowCellComponent {
  private readonly tableData = inject(OcpTableData);

  readonly column = input.required<TableColumnConfig>();
  readonly rowData = input.required<unknown>();
  readonly cellTemplate = input<TemplateRef<{ $implicit: unknown }> | null>(null);

  protected readonly columnWidth = computed(() =>
    getResolvedColumnWidth(
      this.column(),
      this.tableData.columnSizing(),
      this.tableData.columnWidthPercentages()
    )
  );
}
